/**
 * **API documentation** — `/developers/docs` (`specs/M8-P4-developers-docs-playground.md` §6).
 *
 * Left: a search box over one grouped tree (Queries · Mutations · Objects · Inputs · Interfaces
 * · Unions · Enums · Scalars). Right: the selected entry — signature, arguments, fields, enum
 * values, the deprecation banner, the conformance badge with its note, a copy button and
 * *Try in playground*, which hands the generated example to `/developers/playground`.
 *
 * Three decisions worth stating:
 *
 * 1. **The tree is the index, the detail is a second call.** The served SDL is 28 500 lines and
 *    1 346 types; shipping the whole model on every page load would be several megabytes, and a
 *    documentation page that costs that much to open is one nobody opens.
 * 2. **The badge is the point.** Every root field says whether its answer was replayed
 *    byte-identical against monday's own recording, identical in shape, a registered deviation,
 *    or not implemented at all. That is the question somebody porting an integration is
 *    actually asking, and it is the one thing monday's own docs cannot tell them.
 * 3. **Nothing here is written by hand**, so it cannot describe an API we do not serve.
 */
import { Button } from '@elizmonday/ui';
import { useQuery } from '@tanstack/react-query';
import type * as React from 'react';
import { useDeferredValue, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { DeveloperShell } from '../developer';
import { CodeBlock, InlineText } from './blocks';
import {
  BADGE_LABELS,
  BADGE_SHORT,
  KIND_LABELS,
  REFERENCE_KINDS,
  type DevelopersSource,
  type ReferenceBadge,
  type ReferenceEntry,
  type ReferenceKind,
  type ReferenceSummary,
} from './source';
import { useTrpcDevelopersSource } from './trpc-source';
import { DownloadsBar, DocsTabs, useDevelopersSource } from './chrome';
import './developers.css';

/** How many rows one group renders before it says how many more there are. */
const GROUP_CAP = 400;

export function Badge({ badge }: { badge: ReferenceBadge | null }): React.ReactElement | null {
  if (!badge) return null;
  return (
    <span className="ez-docs__badge" data-badge={badge} title={BADGE_LABELS[badge]}>
      {BADGE_SHORT[badge]}
    </span>
  );
}

/** `Query.boards` reads as `boards` in the tree; the type name is the whole row elsewhere. */
const displayName = (summary: { name: string; kind: ReferenceKind }): string =>
  summary.kind === 'query' || summary.kind === 'mutation' ? (summary.name.split('.')[1] ?? summary.name) : summary.name;

function matches(summary: ReferenceSummary, needle: string): boolean {
  if (needle === '') return true;
  return (
    summary.name.toLowerCase().includes(needle) || summary.summary.toLowerCase().includes(needle)
  );
}

interface TreeProps {
  entries: readonly ReferenceSummary[];
  selected: string;
  onSelect(name: string): void;
}

function ReferenceTree({ entries, selected, onSelect }: TreeProps): React.ReactElement {
  const [query, setQuery] = useState('');
  // 1 300 rows re-filtered on every keystroke: let React keep the input responsive.
  const needle = useDeferredValue(query).trim().toLowerCase();
  const [collapsed, setCollapsed] = useState<ReadonlySet<ReferenceKind>>(
    () => new Set<ReferenceKind>(['object', 'input', 'interface', 'union', 'enum', 'scalar']),
  );

  const grouped = useMemo(() => {
    const map = new Map<ReferenceKind, ReferenceSummary[]>();
    for (const kind of REFERENCE_KINDS) map.set(kind, []);
    for (const summary of entries) {
      if (!matches(summary, needle)) continue;
      map.get(summary.kind)?.push(summary);
    }
    return map;
  }, [entries, needle]);

  const total = [...grouped.values()].reduce((sum, rows) => sum + rows.length, 0);

  return (
    <div className="ez-docs__tree" data-testid="developers-tree">
      <div className="ez-docs__search">
        <input
          type="search"
          value={query}
          placeholder="Search the schema"
          aria-label="Search the schema"
          data-testid="developers-search"
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <div className="ez-docs__groups">
        {total === 0 ? (
          <p className="ez-docs__more" data-testid="developers-tree-empty">
            Nothing in the schema matches “{query}”.
          </p>
        ) : null}
        {REFERENCE_KINDS.map((kind) => {
          const rows = grouped.get(kind) ?? [];
          if (rows.length === 0) return null;
          // A search opens every group that has a hit; without one the type groups stay shut.
          const isCollapsed = needle === '' && collapsed.has(kind);
          return (
            <div key={kind}>
              <button
                type="button"
                className="ez-docs__group-head"
                aria-expanded={!isCollapsed}
                data-testid={`developers-group-${kind}`}
                onClick={() =>
                  setCollapsed((previous) => {
                    const next = new Set(previous);
                    if (next.has(kind)) next.delete(kind);
                    else next.add(kind);
                    return next;
                  })
                }
              >
                <span>{KIND_LABELS[kind]}</span>
                <span className="ez-docs__group-count">{rows.length}</span>
              </button>
              {isCollapsed
                ? null
                : rows.slice(0, GROUP_CAP).map((summary) => (
                    <button
                      key={summary.name}
                      type="button"
                      className="ez-docs__row"
                      aria-current={summary.name === selected ? 'true' : undefined}
                      data-testid={`developers-row-${summary.name}`}
                      onClick={() => onSelect(summary.name)}
                    >
                      <span
                        className={`ez-docs__row-name${summary.deprecated ? ' ez-docs__row-deprecated' : ''}`}
                      >
                        {displayName(summary)}
                      </span>
                      <Badge badge={summary.badge} />
                    </button>
                  ))}
              {!isCollapsed && rows.length > GROUP_CAP ? (
                <p className="ez-docs__more">
                  {rows.length - GROUP_CAP} more — narrow the search to see them.
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ArgumentsTable({ entry }: { entry: ReferenceEntry }): React.ReactElement | null {
  if (entry.args.length === 0) return null;
  return (
    <>
      <h3 className="ez-docs__section-title">Arguments</h3>
      <div className="ez-docs__table-wrap">
        <table className="ez-docs__table" data-testid="developers-arguments">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Default</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {entry.args.map((argument) => (
              <tr key={argument.name}>
                <td>
                  <code>{argument.name}</code>
                  {argument.required ? <abbr title="Required"> *</abbr> : null}
                </td>
                <td>
                  <code>{argument.type}</code>
                </td>
                <td>{argument.defaultValue ? <code>{argument.defaultValue}</code> : '—'}</td>
                <td>{argument.description ? <InlineText text={argument.description} /> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function FieldsTable({
  entry,
  onSelect,
}: {
  entry: ReferenceEntry;
  onSelect(name: string): void;
}): React.ReactElement | null {
  if (entry.fields.length === 0) return null;
  return (
    <>
      <h3 className="ez-docs__section-title">Fields</h3>
      <div className="ez-docs__table-wrap">
        <table className="ez-docs__table" data-testid="developers-fields">
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {entry.fields.map((field) => (
              <tr key={field.name}>
                <td>
                  <code className={field.deprecationReason ? 'ez-docs__row-deprecated' : undefined}>
                    {field.name}
                  </code>
                </td>
                <td>
                  {/* Every type name is a link: following a schema is following its types. */}
                  <button type="button" className="ez-docs__link" onClick={() => onSelect(field.typeName)}>
                    {field.type}
                  </button>
                </td>
                <td>
                  {field.description ? <InlineText text={field.description} /> : null}
                  {field.deprecationReason ? (
                    <p className="ez-docs__meta">Deprecated: {field.deprecationReason}</p>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function EnumTable({ entry }: { entry: ReferenceEntry }): React.ReactElement | null {
  if (entry.enumValues.length === 0) return null;
  return (
    <>
      <h3 className="ez-docs__section-title">Values</h3>
      <div className="ez-docs__table-wrap">
        <table className="ez-docs__table" data-testid="developers-enum-values">
          <tbody>
            {entry.enumValues.map((value) => (
              <tr key={value.name}>
                <td>
                  <code>{value.name}</code>
                </td>
                <td>{value.description ? <InlineText text={value.description} /> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

interface DetailProps {
  name: string;
  source: DevelopersSource;
  version: string;
  onSelect(name: string): void;
}

function EntryDetail({ name, source, version, onSelect }: DetailProps): React.ReactElement {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const entry = useQuery({
    queryKey: ['developers', 'entry', name, version],
    queryFn: () => source.entry(name, version),
  });

  if (entry.isPending) {
    return (
      <section className="ez-docs__detail" data-testid="developers-detail">
        <p className="ez-docs__prose">Loading…</p>
      </section>
    );
  }
  if (entry.isError || !entry.data) {
    return (
      <section className="ez-docs__detail" data-testid="developers-detail">
        <p className="ez-docs__prose">
          <code>{name}</code> is not in the {version} schema.
        </p>
      </section>
    );
  }

  const item = entry.data;
  const isRoot = item.kind === 'query' || item.kind === 'mutation';

  const copy = async (): Promise<void> => {
    if (!item.example) return;
    try {
      await navigator.clipboard.writeText(item.example);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* A refused clipboard is not an error worth a dialog; the example is on screen. */
    }
  };

  return (
    <section className="ez-docs__detail" data-testid="developers-detail" data-entry={item.name}>
      <div className="ez-docs__detail-head">
        <span className="ez-docs__kind">{KIND_LABELS[item.kind]}</span>
        <h2>{displayName(item)}</h2>
        <Badge badge={item.badge} />
        <div className="ez-docs__detail-actions">
          {item.example ? (
            <Button kind="tertiary" size="small" onClick={copy} data-testid="developers-copy">
              {copied ? 'Copied' : 'Copy example'}
            </Button>
          ) : null}
          {isRoot && item.example ? (
            <Button
              kind="secondary"
              size="small"
              data-testid="developers-try"
              onClick={() =>
                navigate(`/developers/playground?document=${encodeURIComponent(item.example ?? '')}`)
              }
            >
              Try in playground
            </Button>
          ) : null}
        </div>
      </div>

      {item.deprecationReason ? (
        <p className="ez-docs__deprecated" data-testid="developers-deprecated">
          <strong>Deprecated.</strong> <InlineText text={item.deprecationReason} />
        </p>
      ) : null}

      {item.description ? (
        <p className="ez-docs__prose">
          <InlineText text={item.description} />
        </p>
      ) : null}

      {item.badge ? (
        <p className="ez-docs__meta" data-testid="developers-badge-note">
          {BADGE_LABELS[item.badge]}
          {item.badgeNote ? ` — ${item.badgeNote}` : ''}
          {item.badgeOps.length > 0 ? ` · witnessed by ${item.badgeOps.join(', ')}` : ''}
        </p>
      ) : null}

      {item.signature ? <CodeBlock code={item.signature} testId="developers-signature" /> : null}

      <ArgumentsTable entry={item} />
      <FieldsTable entry={item} onSelect={onSelect} />
      <EnumTable entry={item} />

      {item.interfaces.length > 0 ? (
        <p className="ez-docs__meta">
          Implements:{' '}
          {item.interfaces.map((interfaceName) => (
            <button
              key={interfaceName}
              type="button"
              className="ez-docs__link"
              onClick={() => onSelect(interfaceName)}
            >
              {interfaceName}
            </button>
          ))}
        </p>
      ) : null}

      {item.possibleTypes.length > 0 ? (
        <p className="ez-docs__meta">
          One of:{' '}
          {item.possibleTypes.map((typeName) => (
            <button key={typeName} type="button" className="ez-docs__link" onClick={() => onSelect(typeName)}>
              {typeName}
            </button>
          ))}
        </p>
      ) : null}

      {item.example ? (
        <>
          <h3 className="ez-docs__section-title">Example</h3>
          <CodeBlock code={item.example} testId="developers-example" />
        </>
      ) : null}
    </section>
  );
}

export interface ApiDocsPageProps {
  source?: DevelopersSource;
}

export function ApiDocsPage({ source: injected }: ApiDocsPageProps = {}): React.ReactElement {
  const trpcSource = useTrpcDevelopersSource();
  const source = useDevelopersSource(injected, trpcSource);
  const overview = useQuery({ queryKey: ['developers', 'overview'], queryFn: () => source.overview() });
  const [version, setVersion] = useState<string | null>(null);
  const resolvedVersion = version ?? overview.data?.defaultVersion ?? '2026-10';
  const index = useQuery({
    queryKey: ['developers', 'reference', resolvedVersion],
    queryFn: () => source.reference(resolvedVersion),
  });
  const [selected, setSelected] = useState('Query.boards');

  return (
    <DeveloperShell current="docs">
      <div className="ez-docs" data-testid="developers-docs">
        <h1 className="ez-dev__heading">API documentation</h1>
        <p className="ez-dev__lede">
          Generated from the schema this deployment serves at <code>{overview.data?.endpoint ?? '/v2'}</code>
          . Every query and mutation carries the verdict of the last conformance replay against
          monday’s own recorded answers.
        </p>

        <div className="ez-docs__head">
          <DocsTabs current="reference" />
          <div className="ez-docs__downloads">
            {overview.data ? <DownloadsBar overview={overview.data} version={resolvedVersion} /> : null}
            {(overview.data?.versions.length ?? 0) > 1 ? (
              <label className="ez-play__bar">
                <select
                  aria-label="API version"
                  data-testid="developers-version"
                  value={resolvedVersion}
                  onChange={(event) => setVersion(event.target.value)}
                >
                  {overview.data?.versions.map((value: string) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        </div>

        <div className="ez-docs__body">
          <ReferenceTree
            entries={index.data?.entries ?? []}
            selected={selected}
            onSelect={setSelected}
          />
          <EntryDetail
            name={selected}
            source={source}
            version={resolvedVersion}
            onSelect={setSelected}
          />
        </div>
      </div>
    </DeveloperShell>
  );
}
